const STORAGE_KEY = 'oms.ollieOrderingHandoff'
const HANDOFF_TTL_MS = 5 * 60 * 1000

const HOW_TO = /\bhow (do|can|to|does|would|should)\b/i
const WHERE_TO = /^\s*where (do|can|to|should)\b/i
const FAQ_OPENER = /^\s*(what|why|when|can i)\b/i

const DOOR_ONLY =
  /^(please |can you |could you |would you )?(help me )?(to )?(build|make|place|start)\b.{0,50}\border\b[.!?]*$/i
const FIND_PRODUCTS_DOOR =
  /^(please |can you |could you |would you )?(help me )?(to )?find products?( to order)?[.!?]*$/i
const FIND_NAMED_PRODUCT_DOOR =
  /^(please |can you |could you |would you )?(help me )?(to )?find (?!my |the |our |all |outstanding |invoice|delivery|order|price|supplier|food safety)(.{1,60}?)( to order)?[.!?]*$/i

/** Activation 3.3 — bare product names / short lists (keep in sync with app). */
const PRODUCT_WORD = /^[A-Za-z][A-Za-z0-9'&.%-]*$/
const NOT_PRODUCT_OPENER =
  /^(i |we |you |please |can |could |would |will |tell |show |explain |help |where |how |what |why |when |is |are |do |does |did |should |who |which )/i
const SENTENCE_SIGNAL =
  /\b(is|are|was|were|do|does|did|have|has|had|need|needs|want|wants|order|orders|ordered|everyday|today|tomorrow|please|thanks|thank)\b/i

function isSingleProductPhrase(value, { allowSentenceSignal }) {
  const words = String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (words.length < 1 || words.length > 5) return false
  if (!words.every(word => PRODUCT_WORD.test(word))) return false
  if (!allowSentenceSignal && SENTENCE_SIGNAL.test(value)) return false
  if (allowSentenceSignal && words.length >= 3 && SENTENCE_SIGNAL.test(value)) {
    return false
  }
  return true
}

function isOllieBareProductSearchText(text) {
  const value = String(text || '').trim()
  if (!value) return false
  if (value.length > 100) return false
  if (/[?]/.test(value)) return false
  if (NOT_PRODUCT_OPENER.test(value)) return false
  if (value.includes(',')) {
    const parts = value
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)
    if (parts.length < 2 || parts.length > 8) return false
    return parts.every(part =>
      isSingleProductPhrase(part, { allowSentenceSignal: false }),
    )
  }
  return isSingleProductPhrase(value, { allowSentenceSignal: true })
}

function matchesOrderingDoor(value) {
  return (
    FIND_PRODUCTS_DOOR.test(value) ||
    FIND_NAMED_PRODUCT_DOOR.test(value) ||
    /\bbuild (me |us |my |an |the |a )?(an )?order\b/i.test(value) ||
    /\bmake (me |us )?(an |my |a )?order\b/i.test(value) ||
    /\bstart (an |my |a )?order\b/i.test(value) ||
    /\b(want to|wanna|need to) (place|build|make) (an |a |my )?order\b/i.test(
      value,
    ) ||
    /\bplace (an |my |a )?order with ollie\b/i.test(value) ||
    /\border with ollie\b/i.test(value) ||
    /\b(add|put) .{0,80}\b(in|into|to) (my |the )?basket\b/i.test(value) ||
    /\bi need\s+\d+\b/i.test(value) ||
    /\bget me\s+\d+\b/i.test(value) ||
    /\border me\b/i.test(value) ||
    /\bcan you (build|make|order|add)\b/i.test(value)
  )
}

export function isOllieOrderingHandoffText(text) {
  const value = String(text || '').trim()
  if (!value) return false
  if (HOW_TO.test(value) || WHERE_TO.test(value) || FAQ_OPENER.test(value)) {
    return false
  }
  return matchesOrderingDoor(value) || isOllieBareProductSearchText(value)
}

export function ollieOrderingHandoffDraft(text) {
  const value = String(text || '').trim()
  if (!isOllieOrderingHandoffText(value)) return ''
  if (
    (DOOR_ONLY.test(value) || FIND_PRODUCTS_DOOR.test(value)) &&
    !/\d/.test(value)
  ) {
    return ''
  }
  const named = value.match(FIND_NAMED_PRODUCT_DOOR)
  if (named && !FIND_PRODUCTS_DOOR.test(value)) {
    return String(named[4] || '').trim()
  }
  if (isOllieBareProductSearchText(value)) return value
  return value
}

export function ollieOrderingShoppingListsPath(venueId) {
  return `/v/${venueId}/products?ollieOrder=1`
}

export function parseOllieOrderQuery(search) {
  const value = String(search || '')
  const params = new URLSearchParams(
    value.startsWith('?') ? value.slice(1) : value,
  )
  return params.get('ollieOrder') === '1'
}

export function stripOllieOrderSearch(search) {
  const value = String(search || '')
  const params = new URLSearchParams(
    value.startsWith('?') ? value.slice(1) : value,
  )
  params.delete('ollieOrder')
  const next = params.toString()
  return next ? `?${next}` : ''
}

export function storeOllieOrderingHandoff({ venueId, draft } = {}) {
  if (typeof sessionStorage === 'undefined' || !venueId) return
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      venueId: String(venueId),
      draft: String(draft || ''),
      at: Date.now(),
    }),
  )
}

export function consumeOllieOrderingHandoff(venueId) {
  if (typeof sessionStorage === 'undefined' || !venueId) {
    return { open: false, draft: '' }
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { open: false, draft: '' }
    const parsed = JSON.parse(raw)
    if (String(parsed.venueId) !== String(venueId)) {
      return { open: false, draft: '' }
    }
    sessionStorage.removeItem(STORAGE_KEY)
    if (Date.now() - Number(parsed.at || 0) > HANDOFF_TTL_MS) {
      return { open: false, draft: '' }
    }
    return { open: true, draft: String(parsed.draft || '') }
  } catch (error) {
    return { open: false, draft: '' }
  }
}

export function beginOllieOrderingHandoff({
  venueId,
  text,
  history,
  closeOllie,
} = {}) {
  if (!venueId || !isOllieOrderingHandoffText(text)) return false
  storeOllieOrderingHandoff({
    venueId,
    draft: ollieOrderingHandoffDraft(text),
  })
  closeOllie?.()
  history?.push(ollieOrderingShoppingListsPath(venueId))
  return true
}
